/** Sparziele (BE-05): Kachel für den Überblick und die Liste mit Einzahlen und Prognose. */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/ui/primitives/Button';
import { Input } from '@/ui/primitives/Input';
import { StatKarte } from '@/ui/composites/Statistik';
import { sparzielAnlegen, sparzielEinzahlen, sparzielLoeschen, sparzielRateSetzen } from '@/db/repositories/finanzen';
import { euro, euroParsen } from '@/domain/finanzen/finanzen';
import { prognose, sparAnteil, type Sparziel } from '@/domain/finanzen/sparziele';
import { useFesterBereich } from '@/modules/areas/useFesterBereich';
import { useFinanzen } from '@/modules/finanzen/useFinanzen';
import { formatDe } from '@/lib/datetime';

function prognoseText(s: Sparziel, heute: string, schnitt: number | null): string {
  const p = prognose(s, heute, schnitt);
  if (p.erreicht) return 'Ziel erreicht';
  if (!p.datum) return 'Noch nicht abzusehen – lege eine Sparrate fest oder buche Einnahmen.';
  return `Voraussichtlich ${formatDe(p.datum, 'MMMM yyyy')} (${p.monate} ${p.monate === 1 ? 'Monat' : 'Monate'} bei ${euro(p.rateCent!)} pro Monat, ${p.quelle === 'plan' ? 'geplant' : 'Schnitt der letzten Monate'})`;
}

function Fortschritt({ s }: { s: Sparziel }) {
  const a = sparAnteil(s);
  return (
    <div className="h-2 overflow-hidden rounded-full bg-surface-raised" role="progressbar" aria-label={`Fortschritt ${s.name}`} aria-valuemin={0} aria-valuemax={s.zielCent} aria-valuenow={Math.min(s.gespartCent, s.zielCent)}>
      <div className="h-full rounded-full bg-accent" style={{ width: `${Math.round(a * 100)}%` }} />
    </div>
  );
}

export function FinanzSparzieleKachel() {
  const { sparziele, heute, schnittSparrate } = useFinanzen();
  return (
    <StatKarte titel="Sparziele" frage="Wann erreiche ich mein Ziel?" aktion={<Link to="/finanzen/sparziele" className="text-[12px] text-accent-text hover:underline">Sparziele</Link>}>
      {sparziele.length === 0 ? <p className="text-sm text-ink-faint">Noch kein Sparziel angelegt.</p> : (
        <ul className="flex flex-col gap-3">
          {sparziele.slice(0, 3).map((s) => (
            <li key={s.id} className="flex flex-col gap-1 text-sm">
              <div className="flex items-baseline gap-2"><span className="min-w-0 flex-1 truncate">{s.name}</span><span className="tabular-nums">{euro(s.gespartCent)}<span className="text-ink-faint"> von {euro(s.zielCent)}</span></span></div>
              <Fortschritt s={s} />
              <span className="text-[11px] text-ink-faint">{prognoseText(s, heute, schnittSparrate)}</span>
            </li>
          ))}
        </ul>
      )}
    </StatKarte>
  );
}

function SparzielZeile({ s }: { s: Sparziel }) {
  const { heute, schnittSparrate } = useFinanzen();
  const [betrag, setBetrag] = useState('');
  const [rate, setRate] = useState<string | null>(null);
  const cent = euroParsen(betrag);
  return (
    <li className="flex flex-col gap-1.5 py-2.5 text-sm">
      <div className="flex items-baseline gap-2">
        <span className="min-w-0 flex-1 truncate font-medium">{s.name}</span>
        <span className="tabular-nums">{euro(s.gespartCent)}<span className="text-ink-faint"> von {euro(s.zielCent)}</span></span>
        <button type="button" className="grid size-7 place-items-center rounded text-ink-faint hover:text-danger" aria-label={`Sparziel ${s.name} löschen`} onClick={() => void sparzielLoeschen(s)}><Trash2 className="size-3.5" /></button>
      </div>
      <Fortschritt s={s} />
      <p className="text-[12px] text-ink-muted">{prognoseText(s, heute, schnittSparrate)}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input aria-label={`Betrag für ${s.name}`} inputMode="decimal" placeholder="Betrag" className="h-8 w-24" value={betrag} onChange={(e) => setBetrag(e.target.value)} />
        <Button size="sm" disabled={!cent} onClick={() => { void sparzielEinzahlen(s.id, cent!); setBetrag(''); }}><Plus className="size-4" /> Einzahlen</Button>
        <Button size="sm" variant="ghost" disabled={!cent} onClick={() => { void sparzielEinzahlen(s.id, -cent!); setBetrag(''); }}><Minus className="size-4" /> Entnehmen</Button>
        <label className="ml-auto flex items-center gap-1.5 text-[12px] text-ink-muted">Rate/Monat
          <Input aria-label={`Sparrate ${s.name} in Euro pro Monat`} inputMode="decimal" placeholder="Schnitt" className="h-8 w-20 px-2 text-[12px]" value={rate ?? (s.monatlichCent ? String(s.monatlichCent / 100).replace('.', ',') : '')}
            onChange={(e) => setRate(e.target.value)} onBlur={() => { if (rate !== null) { void sparzielRateSetzen(s.id, euroParsen(rate) ?? undefined); setRate(null); } }} />
        </label>
      </div>
    </li>
  );
}

export function SparzielListe() {
  const f = useFinanzen();
  const area = useFesterBereich('finanzen');
  const [name, setName] = useState('');
  const [ziel, setZiel] = useState('');
  const zielCent = euroParsen(ziel);
  return (
    <>
      <StatKarte titel="Sparziele" frage="Wofür spare ich, und wie weit bin ich?">
        {f.sparziele.length === 0 && <p className="text-sm text-ink-faint">Noch kein Sparziel, z. B. „Laptop“ mit 900 €.</p>}
        <ul className="flex flex-col divide-y divide-line">{f.sparziele.map((s) => <SparzielZeile key={s.id} s={s} />)}</ul>
      </StatKarte>
      <StatKarte titel="Neues Sparziel">
        <form className="flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); if (name.trim() && zielCent && area) { void sparzielAnlegen(area.id, name, zielCent); setName(''); setZiel(''); } }}>
          <Input aria-label="Name des Sparziels" placeholder="Wofür?" value={name} onChange={(e) => setName(e.target.value)} className="min-w-40 flex-1" />
          <Input aria-label="Zielbetrag in Euro" inputMode="decimal" placeholder="Zielbetrag" className="w-28" value={ziel} onChange={(e) => setZiel(e.target.value)} />
          <Button type="submit" variant="primary" disabled={!name.trim() || !zielCent || !area}><Plus className="size-4" /> Anlegen</Button>
        </form>
      </StatKarte>
    </>
  );
}
